import { useEffect, useState } from "react";
import axios from "axios";
import NavBar from "../components/NavBar";
import { Link } from "react-router-dom";
import { BrandLine } from "../components/RequestTools";
import { StaySummary } from "../components/StayFields";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending: { label: "En attente", color: "bg-amber-50 text-amber-800 border-amber-200" },
  sent: { label: "Envoyée à la résidence", color: "bg-sky-50 text-sky-800 border-sky-200" },
  replied: { label: "Réponse reçue", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  chosen: { label: "Résidence choisie", color: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  accepted: { label: "Acceptée", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  declined: { label: "Refusée", color: "bg-rose-50 text-rose-800 border-rose-200" },
  closed: { label: "Clôturée", color: "bg-stone-100 text-stone-700 border-stone-300" },
};

const frDate = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
const UNIT = { month: "/ mois", night: "/ nuit" };

const offerName = (o) => [o.brand, o.residence !== o.title ? o.residence : ""].filter(Boolean).join(" · ");

function ChooseButton({ r, o, onDone }) {
  const [busy, setBusy] = useState(false);
  const choose = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/reservations/${r.id}/choose`, { listing_id: o.listing_id }, { withCredentials: true });
      toast.success("Choix confirmé : la résidence et matchappart sont prévenues");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'enregistrer votre choix");
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" disabled={busy} className="btn-primary px-4 py-2 rounded-full text-xs font-medium" data-testid={`choose-btn-${o.listing_id}`}>Je choisis cette résidence</button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="choose-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif">Confirmer {offerName(o) || o.title} ?</AlertDialogTitle>
          <AlertDialogDescription>
            La résidence est prévenue immédiatement pour finaliser votre réservation. Ce choix est définitif et ne pourra pas être modifié depuis votre espace.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="choose-cancel">Annuler</AlertDialogCancel>
          <AlertDialogAction onClick={choose} data-testid="choose-confirm">Oui, je choisis cette résidence</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Reply({ r, onDone }) {
  const reply = r.reply;
  const chosenId = r.chosen?.listing_id;
  return (
    <div className="mt-5 border-t border-[#E5E1D8] pt-4" data-testid="reservation-reply">
      <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4F6B4A]">
        Réponse matchappart · {new Date(reply.at).toLocaleDateString("fr-FR")}
      </div>
      <p className="text-sm text-[#2B3029] mt-2">{reply.offers.length > 1 ? "Les résidences disponibles" : "La résidence disponible"} pour votre demande :</p>
      <ul className="mt-3 space-y-2">
        {reply.offers.map((o, i) => (
          <li key={i} className={`rounded-md px-4 py-3 flex flex-wrap items-center justify-between gap-3 ${chosenId === o.listing_id && chosenId ? "bg-emerald-50 ring-1 ring-emerald-300" : "bg-[#F5F3ED]"} ${chosenId && chosenId !== o.listing_id ? "opacity-50" : ""}`} data-testid={`reply-offer-${i}`}>
            <div className="min-w-0">
              <div className="font-medium text-[#1A1A1A]">{offerName(o) || o.title} {offerName(o) && <span className="font-normal text-[#666]">— {o.title}</span>}</div>
              {o.address && <div className="text-xs text-[#666] mt-0.5">{o.address}</div>}
              {o.listing_id && <Link to={`/listings/${o.listing_id}`} className="text-xs text-[#8C6C50] underline">Voir l'annonce</Link>}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="font-serif text-lg text-[#2B3029] whitespace-nowrap">
                {o.price != null ? `${Math.round(o.price).toLocaleString("fr-FR")} € ${UNIT[o.unit] || ""}` : <span className="text-sm text-[#666]">Tarif sur demande</span>}
                {o.price_night != null && <span className="text-sm text-[#666]"> · {Math.round(o.price_night).toLocaleString("fr-FR")} € / nuit</span>}
              </div>
              {o.counter && <span className="text-[11px] text-[#8C6C50]" data-testid={`reply-offer-counter-${i}`}>Tarif proposé par la résidence pour vos dates</span>}
              {!chosenId && o.listing_id && <ChooseButton r={r} o={o} onDone={onDone} />}
              {chosenId === o.listing_id && chosenId && <span className="text-xs font-semibold text-emerald-800" data-testid="chosen-label">Votre choix · le {new Date(r.chosen.at).toLocaleDateString("fr-FR")}</span>}
              {chosenId === o.listing_id && chosenId && (
                <span className={`text-[11px] ${r.chosen.confirmed_at ? "text-emerald-800 font-semibold" : "text-[#8C6C50]"}`} data-testid="chosen-confirmation">
                  {r.chosen.confirmed_at ? `Réservation confirmée par la résidence le ${new Date(r.chosen.confirmed_at).toLocaleDateString("fr-FR")}` : "En attente de confirmation de la résidence"}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
      {reply.note && <p className="mt-3 text-sm text-[#2B3029] whitespace-pre-line" data-testid="reply-note">{reply.note}</p>}
      <p className="mt-3 text-xs text-[#666]">Pour réserver ou poser une question, répondez à l'email reçu ou écrivez à contact@matchappart.com.</p>
    </div>
  );
}

export default function MyReservations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/reservations/mine`, { withCredentials: true });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#F5F3ED]">
      <NavBar />
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="text-xs tracking-[0.25em] uppercase font-semibold text-[#8C6C50]">Suivi</div>
        <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter mt-4 text-[#1A1A1A]">Mes demandes de réservation</h1>

        {loading && <div className="mt-10 text-sm text-[#666]">Chargement…</div>}
        {!loading && rows.length === 0 && (
          <div className="mt-10 bg-white border border-[#E5E1D8] p-10 text-center rounded-md" data-testid="reservations-empty">
            <p className="text-sm text-[#666]">Vous n'avez pas encore formulé de demande.</p>
            <Link to="/catalog" className="btn-primary inline-block mt-5 px-6 py-2.5 rounded-full text-sm font-medium">
              Explorer le catalogue
            </Link>
          </div>
        )}

        <div className="mt-10 space-y-4" data-testid="my-reservations-list">
          {rows.map((r) => {
            const early = ["pending", "sent"].includes(r.status);
            const s = (early && r.chosen ? STATUS_LABELS.chosen : early && r.reply ? STATUS_LABELS.replied : STATUS_LABELS[r.status]) || STATUS_LABELS.pending;
            return (
              <div key={r.id} className="bg-white border border-[#E5E1D8] p-6 rounded-md" data-testid={`my-reservation-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 basis-full sm:basis-0 sm:flex-1">
                  <div className="font-serif text-xl text-[#1A1A1A]">{r.listing_title}</div>
                  <BrandLine r={r} />
                  <div className="text-sm text-[#666] mt-1">{r.listing_city}</div>
                  <div className="mt-3 text-sm text-[#2B3029] flex flex-wrap gap-x-6 gap-y-1">
                    <StaySummary r={r} />
                    <span>Personnes : <strong>{r.guests}</strong></span>
                  </div>
                  {r.message && <p className="mt-3 text-sm text-[#666] italic">« {r.message} »</p>}
                </div>
                <span className={`text-xs tracking-wider uppercase font-semibold px-3 py-1.5 rounded-full border ${s.color}`} data-testid={`my-reservation-status-${r.id}`}>
                  {s.label}
                </span>
              </div>
              {r.reply && <Reply r={r} onDone={load} />}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
